import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { transform } from 'entities/experiences';
import { generatePreviewStep } from 'lib/templates';
import Editor from 'components/Editor/Editor';
import PreviewProvider from './PreviewProvider';

function deriveStepData(experience, stepId) {
  if (!experience || !stepId) return {};

  const { stepChildren, stepGroups } = transform(experience);
  const { content, traits } = stepChildren[stepId] || {};

  const stepGroupId = Object.keys(stepGroups).find(section =>
    stepGroups[section].children.includes(stepId)
  );

  const { editor } = stepGroups[stepGroupId] || {};

  return { stepChildren, editor, content, traits };
}

export default function PreviewExperience({
  experience: initialExperience,
  stepId: initialStepId,
  preferences: initialPreferences,
} = {}) {
  const initialData = useMemo(
    () => deriveStepData(initialExperience, initialStepId),
    [initialExperience, initialStepId]
  );

  const [userPreferences, setUserPreferences] = useState(
    initialPreferences || {}
  );
  const [editorValue, setEditorValue] = useState(initialData.editor);
  const [contentValue, setContentValue] = useState(initialData.content);
  const [traitsValue, setTraitsValue] = useState(initialData.traits);
  const [stepChildrenStore, setStepChildren] = useState(
    initialData.stepChildren
  );
  const [stepChild, setStepChild] = useState(initialStepId);

  // Sync state when props change (canvas-preview step navigation)
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!initialExperience || !initialStepId) return;

    const data = deriveStepData(initialExperience, initialStepId);
    setStepChild(initialStepId);
    setStepChildren(data.stepChildren);
    setContentValue(data.content);
    setTraitsValue(data.traits);
    setEditorValue(data.editor);
  }, [initialExperience, initialStepId]);

  useEffect(() => {
    if (initialPreferences) setUserPreferences(initialPreferences);
  }, [initialPreferences]);

  // Exposed for image-service / Puppeteer
  window.apc_set_mobile_experience = (experience, stepId, preferences) => {
    document.documentElement.style.backgroundColor = 'transparent';

    const data = deriveStepData(experience, stepId);

    setUserPreferences(preferences);
    setStepChild(stepId);
    setStepChildren(data.stepChildren);
    setContentValue(data.content);
    setTraitsValue(data.traits);
    setEditorValue(data.editor);
  };

  window.apc_set_template = template => {
    const { editor, content, traits } = generatePreviewStep({
      content: template.content,
    });

    setContentValue(content);
    setTraitsValue(traits);
    setEditorValue(editor);
  };

  return contentValue && editorValue ? (
    <PreviewProvider stepChildren={stepChildrenStore} stepChild={stepChild}>
      <Editor
        editor={editorValue}
        content={contentValue}
        traits={traitsValue}
        userPreferences={userPreferences}
        isImageService
      />
    </PreviewProvider>
  ) : null;
}

PreviewExperience.propTypes = {
  experience: PropTypes.shape({
    steps: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  stepId: PropTypes.string,
  preferences: PropTypes.shape({}),
};
